import React, {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Info, RefreshCw, Loader2, Film, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { NeonBackground } from "@/components/reelflo/NeonBackground";
import { AppShell } from "@/components/reelflo/AppShell";
import { VideoLibraryCard } from "@/components/reelflo/VideoLibraryCard";
import {
    VideoPlayerDialog,
    PLAYER_COPY,
} from "@/components/reelflo/VideoPlayerDialog";

const RETENTION_NOTICE =
    "Finished videos are stored for 45 days after processing. Download anything you want to keep.";

const isExpiredResponse = (err) => {
    const status = err && err.response && err.response.status;
    const code = err && err.response && err.response.data && err.response.data.code;
    return status === 410 || code === "video_unavailable";
};

// Finished-video deep-link ids (focus=<video-hub job_id>, completion=<videos.id>)
// must be a non-empty, safe token. Anything else is ignored so the page behaves
// as an ordinary /videos visit. Ids are matched by EXACT equality against
// video.job_id and used only in a ref map + API path — NEVER to build a CSS
// selector from URL text.
const DEEP_LINK_ID_RE = /^[A-Za-z0-9_-]{1,128}$/;

// The deep-link target is "top-aligned" only when it sits within a small
// tolerance BENEATH the sticky header (cards carry scroll-mt-28 = 112px). A
// merely-visible card that has drifted toward the middle of the viewport is NOT
// accepted — visibility alone is not correct positioning. Re-alignment is
// bounded so a persistent layout shift can never cause a scroll/ack loop.
const DEEP_LINK_TOP_MAX_PX = 160;
const DEEP_LINK_MAX_ALIGN_ATTEMPTS = 3;

export default function Videos() {
    const navigate = useNavigate();

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);

    // Single-player state (one dialog for the whole page).
    const [playerOpen, setPlayerOpen] = useState(false);
    const [playerJob, setPlayerJob] = useState(null); // { job_id, title }
    const [playbackSrc, setPlaybackSrc] = useState(null);
    const [playbackLoading, setPlaybackLoading] = useState(false);
    const [playbackError, setPlaybackError] = useState(null);
    const [playbackRetryable, setPlaybackRetryable] = useState(false);

    // Request-generation guard + abort controller: any response whose generation
    // is no longer current (because the player closed, a new video was selected,
    // or Retry fired) is ignored and can never assign a source or reopen.
    const genRef = useRef(0);
    const abortRef = useRef(null);
    // The element (card Play button) to return keyboard focus to on player close.
    const returnFocusRef = useRef(null);

    // --- Finished-video exact deep-link ------------------------------------ //
    // Parsed ONCE from the URL and strictly validated. focus is the exact card to
    // reveal; completion is the one-time flag to clear. The ack fires ONLY after
    // the target card is mounted in the DOM (see registerFocusCard).
    const [deepLink] = useState(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            const f = (p.get("focus") || "").trim();
            const c = (p.get("completion") || "").trim();
            return {
                focus: DEEP_LINK_ID_RE.test(f) ? f : null,
                completion: DEEP_LINK_ID_RE.test(c) ? c : null,
            };
        } catch (_e) {
            return { focus: null, completion: null };
        }
    });
    const focusJobId = deepLink.focus;
    const completionId = deepLink.completion;

    const [highlightId, setHighlightId] = useState(null);
    const [focusFetching, setFocusFetching] = useState(false);
    const [focusError, setFocusError] = useState(false);
    const [ackState, setAckState] = useState("idle"); // idle | pending | done | error

    const focusFetchTriedRef = useRef(false);
    const focusHandledRef = useRef(false);
    const ackInFlightRef = useRef(false);
    const ackDoneRef = useRef(false);
    const highlightTimerRef = useRef(null);

    // Rewrite the URL to retain focus but drop completion, so a refresh never
    // re-acks. Never reloads the page.
    const dropCompletionFromUrl = useCallback(() => {
        try {
            const p = new URLSearchParams(window.location.search);
            p.delete("completion");
            const qs = p.toString();
            window.history.replaceState(
                {},
                "",
                `${window.location.pathname}${qs ? `?${qs}` : ""}`,
            );
        } catch (_e) {
            /* noop */
        }
    }, []);

    // Acknowledge the completion. Guarded so it fires at most once automatically
    // and can ONLY be re-attempted by an explicit user Retry (never an auto-loop).
    const acknowledgeCompletion = useCallback(() => {
        if (!completionId) return;
        if (ackInFlightRef.current || ackDoneRef.current) return;
        ackInFlightRef.current = true;
        setAckState("pending");
        api
            .post(`/videos/${encodeURIComponent(completionId)}/ack-completion`)
            .then(() => {
                ackDoneRef.current = true;
                setAckState("done");
                dropCompletionFromUrl(); // retain focus, drop completion only
            })
            .catch(() => {
                setAckState("error"); // retain completion in the URL for Retry
            })
            .finally(() => {
                ackInFlightRef.current = false;
            });
    }, [completionId, dropCompletionFromUrl]);

    // Callback ref attached ONLY to the exact focus target's card. It records the
    // node and flags readiness — ALL scroll / focus / highlight / ack work is
    // deferred to the settled effect below, so nothing ever runs against a
    // still-settling layout.
    const focusNodeRef = useRef(null);
    const [focusNodeReady, setFocusNodeReady] = useState(false);
    const registerFocusCard = useCallback((node) => {
        focusNodeRef.current = node || null;
        setFocusNodeReady(!!node);
    }, []);

    // Reveal + acknowledge, but ONLY once the list load and any single-item
    // fallback have settled AND the exact target node exists in the DOM. Uses a
    // React ref only (never a selector built from URL text), DETERMINISTIC top
    // alignment below the sticky header (scroll-mt on the card; no smooth/center
    // drift), verifies the card is actually positioned in the viewport, and only
    // THEN acknowledges the matching completion notice. Runs exactly once.
    useEffect(() => {
        if (!focusJobId || focusHandledRef.current) return;
        if (loading || focusFetching) return; // wait for settle
        const node = focusNodeRef.current;
        if (!node) return; // the exact ref must exist
        // Exact identity guard: the ref is only attached to the matching card,
        // and we re-confirm the rendered job_id equals focus before acting.
        if (node.dataset && node.dataset.jobId && node.dataset.jobId !== focusJobId) {
            return;
        }
        focusHandledRef.current = true;

        const alignTop = () => {
            try {
                node.scrollIntoView({ block: "start", behavior: "auto" });
            } catch (_e) {
                /* noop */
            }
        };
        alignTop();
        try {
            node.focus({ preventScroll: true });
        } catch (_e) {
            /* noop */
        }

        setHighlightId(focusJobId);
        if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        highlightTimerRef.current = setTimeout(() => setHighlightId(null), 2600);

        // Keep the exact target TOP-ALIGNED beneath the sticky header before
        // acknowledging. A card that is merely visible (e.g. drifted into the
        // middle by a post-render layout shift) is REJECTED: re-align to the top
        // and re-verify on the NEXT animation frame. Acknowledgment fires ONLY
        // after a check confirms the card is within the small top tolerance, and
        // re-alignment is capped so no scroll/ack loop is possible. The ack
        // itself is idempotent (guarded in acknowledgeCompletion).
        let alignAttempts = 0;
        const verifyTopAlignedThenAck = () => {
            let rect = null;
            try {
                rect = node.getBoundingClientRect();
            } catch (_e) {
                rect = null;
            }
            const topAligned =
                !!rect && rect.bottom > 0 && rect.top <= DEEP_LINK_TOP_MAX_PX;
            if (topAligned || alignAttempts >= DEEP_LINK_MAX_ALIGN_ATTEMPTS) {
                acknowledgeCompletion();
                return;
            }
            alignAttempts += 1;
            alignTop(); // still visible but drifted -> re-align to the top
            if (typeof requestAnimationFrame === "function") {
                requestAnimationFrame(verifyTopAlignedThenAck);
            } else {
                verifyTopAlignedThenAck();
            }
        };
        if (typeof requestAnimationFrame === "function") {
            requestAnimationFrame(verifyTopAlignedThenAck);
        } else {
            verifyTopAlignedThenAck();
        }
    }, [focusJobId, loading, focusFetching, focusNodeReady, acknowledgeCompletion]);

    // Fallback fetch: if the deep-link target is not in the loaded list, fetch it
    // exactly ONCE (no retry loop) via the single-video endpoint and merge it so
    // it renders. The single endpoint omits retention fields, so a READY item is
    // marked available to render; anything else is an honest "couldn't locate it".
    useEffect(() => {
        if (!focusJobId) return;
        if (loading || loadError) return;
        if (videos.some((v) => v.job_id === focusJobId)) return;
        if (focusFetchTriedRef.current) return;
        focusFetchTriedRef.current = true;
        setFocusFetching(true);
        api
            .get(`/video-hub/${encodeURIComponent(focusJobId)}`)
            .then(({ data }) => {
                if (data && data.job_id === focusJobId && data.status === "ready") {
                    const merged = {
                        ...data,
                        retention_status: "available",
                        available_until: data.available_until || null,
                    };
                    setVideos((prev) =>
                        prev.some((v) => v.job_id === focusJobId)
                            ? prev
                            : [merged, ...prev],
                    );
                } else {
                    setFocusError(true);
                }
            })
            .catch(() => setFocusError(true))
            .finally(() => setFocusFetching(false));
    }, [focusJobId, loading, loadError, videos]);

    // Clear the highlight timer on unmount.
    useEffect(
        () => () => {
            if (highlightTimerRef.current) clearTimeout(highlightTimerRef.current);
        },
        [],
    );

    const fetchVideos = useCallback(async () => {
        setLoading(true);
        setLoadError(false);
        try {
            const { data } = await api.get("/video-hub");
            setVideos(Array.isArray(data && data.videos) ? data.videos : []);
        } catch (_e) {
            setLoadError(true);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchVideos();
    }, [fetchVideos]);

    // Mark ONLY the given card expired (410 outcome). Preserves list order.
    const markExpired = useCallback((jobId) => {
        setVideos((prev) =>
            prev.map((v) =>
                v.job_id === jobId
                    ? { ...v, retention_status: "expired", available_until: null }
                    : v,
            ),
        );
    }, []);

    // Preserve the backend's order; only split into Available then Expired.
    // Items with retention_status null are still processing -> excluded here.
    const { available, expired } = useMemo(() => {
        const av = [];
        const ex = [];
        for (const v of videos) {
            if (v.retention_status === "available") av.push(v);
            else if (v.retention_status === "expired") ex.push(v);
        }
        return { available: av, expired: ex };
    }, [videos]);

    const abortPending = useCallback(() => {
        if (abortRef.current) {
            try {
                abortRef.current.abort();
            } catch (_e) {
                /* noop */
            }
            abortRef.current = null;
        }
    }, []);

    const closePlayer = useCallback(() => {
        // Invalidate any in-flight request so its late response is ignored and
        // can neither reopen the dialog nor assign a source.
        genRef.current += 1;
        abortPending();
        setPlayerOpen(false);
        setPlayerJob(null);
        setPlaybackSrc(null);
        setPlaybackError(null);
        setPlaybackRetryable(false);
        setPlaybackLoading(false);
    }, [abortPending]);

    // Always requests a FRESH short-lived URL; never reuses a previous one.
    const requestPlayback = useCallback(
        (job) => {
            const myGen = (genRef.current += 1);
            abortPending();
            const controller =
                typeof AbortController !== "undefined" ? new AbortController() : null;
            abortRef.current = controller;
            setPlaybackSrc(null);
            setPlaybackError(null);
            setPlaybackRetryable(false);
            setPlaybackLoading(true);
            api
                .get(`/video-hub/${job.job_id}/playback-url`, {
                    signal: controller ? controller.signal : undefined,
                })
                .then(({ data }) => {
                    if (myGen !== genRef.current) return; // stale / aborted / closed
                    setPlaybackLoading(false);
                    setPlaybackSrc((data && data.playback_url) || null);
                })
                .catch((err) => {
                    if (myGen !== genRef.current) return; // stale / aborted / closed
                    setPlaybackLoading(false);
                    if (isExpiredResponse(err)) {
                        markExpired(job.job_id);
                        setPlaybackError(PLAYER_COPY.expired);
                        setPlaybackRetryable(false);
                    } else {
                        setPlaybackError(PLAYER_COPY.service);
                        setPlaybackRetryable(true);
                    }
                });
        },
        [abortPending, markExpired],
    );

    const handlePlay = useCallback(
        (video, triggerEl) => {
            returnFocusRef.current = triggerEl || null;
            setPlayerJob({ job_id: video.job_id, title: video.title });
            setPlayerOpen(true);
            requestPlayback(video);
        },
        [requestPlayback],
    );

    const handleRetryPlay = useCallback(() => {
        if (playerJob) requestPlayback(playerJob);
    }, [playerJob, requestPlayback]);

    // Fresh URL every click. The minted URL is kept in a LOCAL variable only and
    // is never written to React state, storage, history, query params, or logs.
    const handleDownload = useCallback(
        async (video) => {
            try {
                const { data } = await api.get(
                    `/video-hub/${video.job_id}/download-url`,
                );
                const url = data && data.download_url;
                if (!url) {
                    toast.error(PLAYER_COPY.service);
                    return;
                }
                const a = document.createElement("a");
                a.href = url;
                a.rel = "noopener";
                a.style.display = "none";
                document.body.appendChild(a);
                a.click();
                a.remove();
            } catch (err) {
                if (isExpiredResponse(err)) {
                    markExpired(video.job_id);
                    toast.error(PLAYER_COPY.expired);
                } else {
                    toast.error(PLAYER_COPY.service);
                }
            }
        },
        [markExpired],
    );

    const hasAny = available.length > 0 || expired.length > 0;

    return (
        <div className="relative min-h-screen overflow-hidden bg-black text-white">
            <NeonBackground intensity={0.35} />

            <AppShell
                active="videos"
                onLogoClick={() => navigate("/")}
                onNavigate={(key) =>
                    navigate(
                        key === "videos"
                            ? "/videos"
                            : key === "account"
                              ? "/account"
                              : "/dashboard",
                    )
                }
            >

            <main className="relative z-10 mx-auto w-full max-w-6xl px-4 py-8 sm:px-6">
                <div className="mb-6">
                    <h1 className="rf-display text-3xl uppercase sm:text-4xl">
                        Finished Videos
                    </h1>
                    <p
                        data-testid="videos-retention-notice"
                        className="mt-2 flex items-start gap-2 text-sm text-white/55"
                    >
                        <Info className="mt-0.5 h-4 w-4 flex-none text-cyan-300/70" />
                        <span>{RETENTION_NOTICE}</span>
                    </p>
                </div>

                {focusJobId &&
                    (focusFetching || focusError || ackState === "error") && (
                        <div
                            data-testid="deep-link-status"
                            className="mb-6 rounded-lg border border-white/12 bg-surface-panel p-4"
                        >
                            {focusFetching && (
                                <p
                                    data-testid="deep-link-locating"
                                    className="flex items-center gap-2 text-sm text-white/70"
                                >
                                    <Loader2 className="h-4 w-4 animate-spin text-cyan-300" />
                                    Locating your finished video…
                                </p>
                            )}
                            {!focusFetching && focusError && (
                                <p
                                    data-testid="deep-link-not-found"
                                    className="flex items-start gap-2 text-sm text-amber-100/90"
                                >
                                    <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
                                    We couldn't locate that finished video. It may
                                    still be processing or is no longer available.
                                </p>
                            )}
                            {!focusFetching &&
                                !focusError &&
                                ackState === "error" && (
                                    <div
                                        data-testid="deep-link-ack-error"
                                        className="flex flex-wrap items-center justify-between gap-3"
                                    >
                                        <p className="flex items-start gap-2 text-sm text-amber-100/90">
                                            <AlertTriangle className="mt-0.5 h-4 w-4 flex-none text-amber-300" />
                                            Your video is here, but we couldn't
                                            update your dashboard notice.
                                        </p>
                                        <button
                                            type="button"
                                            data-testid="deep-link-ack-retry"
                                            onClick={acknowledgeCompletion}
                                            className="inline-flex items-center gap-2 rounded-lg border border-white/15 min-h-11 px-3 text-sm font-semibold text-white/85 transition-colors hover:border-cyan-300/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                                        >
                                            <RefreshCw className="h-4 w-4" />
                                            Retry
                                        </button>
                                    </div>
                                )}
                        </div>
                    )}

                {loading && (
                    <div
                        data-testid="videos-loading"
                        className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3"
                    >
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div
                                key={i}
                                className="flex flex-col overflow-hidden rounded-lg border border-white/10 bg-surface-panel"
                            >
                                <div className="aspect-video w-full animate-pulse bg-white/5" />
                                <div className="flex flex-col gap-2 p-4">
                                    <div className="h-4 w-3/4 animate-pulse rounded bg-white/5" />
                                    <div className="h-3 w-1/2 animate-pulse rounded bg-white/5" />
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {!loading && loadError && (
                    <div
                        data-testid="videos-error"
                        className="flex flex-col items-center gap-4 rounded-lg border border-white/10 bg-surface-panel p-10 text-center"
                    >
                        <AlertTriangle className="h-8 w-8 text-amber-300" />
                        <p className="text-sm text-white/70">
                            We couldn't load your videos right now.
                        </p>
                        <button
                            type="button"
                            data-testid="videos-retry"
                            onClick={fetchVideos}
                            className="inline-flex items-center gap-2 rounded-lg border border-white/15 min-h-11 px-4 text-sm font-semibold text-white/85 transition-colors hover:border-cyan-300/60 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/60"
                        >
                            <RefreshCw className="h-4 w-4" />
                            Retry
                        </button>
                    </div>
                )}

                {!loading && !loadError && !hasAny && (
                    <div
                        data-testid="videos-empty"
                        className="flex flex-col items-center gap-4 rounded-lg border border-white/10 bg-surface-panel p-10 text-center"
                    >
                        <Film className="h-8 w-8 text-white/30" />
                        <p className="text-sm text-white/70">
                            No finished videos yet. Once a video finishes
                            processing, it will appear here.
                        </p>
                    </div>
                )}

                {!loading && !loadError && hasAny && (
                    <div className="flex flex-col gap-10">
                        {available.length > 0 && (
                            <section data-testid="videos-available-section">
                                <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/60">
                                    Available
                                </h2>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    {available.map((v) => (
                                        <VideoLibraryCard
                                            key={v.job_id}
                                            video={v}
                                            onPlay={handlePlay}
                                            onDownload={handleDownload}
                                            ref={
                                                v.job_id === focusJobId
                                                    ? registerFocusCard
                                                    : undefined
                                            }
                                            highlighted={highlightId === v.job_id}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}

                        {expired.length > 0 && (
                            <section data-testid="videos-expired-section">
                                <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-white/40">
                                    Expired
                                </h2>
                                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                    {expired.map((v) => (
                                        <VideoLibraryCard
                                            key={v.job_id}
                                            video={v}
                                            onPlay={handlePlay}
                                            onDownload={handleDownload}
                                            ref={
                                                v.job_id === focusJobId
                                                    ? registerFocusCard
                                                    : undefined
                                            }
                                            highlighted={highlightId === v.job_id}
                                        />
                                    ))}
                                </div>
                            </section>
                        )}
                    </div>
                )}
            </main>
            </AppShell>

            <VideoPlayerDialog
                open={playerOpen}
                title={playerJob ? playerJob.title : null}
                src={playbackSrc}
                loading={playbackLoading}
                error={playbackError}
                onClose={closePlayer}
                onRetry={playbackRetryable ? handleRetryPlay : null}
                returnFocusRef={returnFocusRef}
            />
        </div>
    );
}
